import {
  EMAIL_CAPTURE_PILL_FORM_CLASS,
  EMAIL_CAPTURE_PILL_INPUT_CLASS,
} from "@/components/forms/email-capture-pill-styles";
import { currencyFor } from "@/components/home/eligibility-currency";
import {
  fundingEligibilityBandScale,
  getFundingEligibilityMultipliers,
} from "@/components/home/funding-eligibility-category-multipliers";
import { formatLocalizedCurrency } from "@/lib/localize-currency";
import { cn } from "@/lib/utils";

const FUNDING_ELIGIBILITY_AUD_COUNTRY = "Australia";

/**
 * Maximum initial advance the calculator will ever display, in EUR.
 * USD, GBP, EUR, CAD, and AUD reuse this figure's literal digits (no FX
 * conversion — $20M, £20M, €20M, C$20M, A$20M). SEK and DKK diverge enough
 * in magnitude from EUR that the same digits would misrepresent the real
 * cap, so those two are approximated via FX and rounded to a nice whole
 * number instead (reviewed 2026-09-07, ~7.46 DKK/EUR and ~11 SEK/EUR).
 */
export const MAX_ADVANCE_EUR = 20_000_000;

const MAX_ADVANCE_BY_CURRENCY: Partial<Record<string, number>> = {
  DKK: 150_000_000,
  SEK: 220_000_000,
};

export function maxAdvanceFor(currencyCode: string): number {
  return MAX_ADVANCE_BY_CURRENCY[currencyCode] ?? MAX_ADVANCE_EUR;
}

/**
 * Generous sanity bound on the monthly revenue input — above this, the
 * figure is almost certainly a typo rather than a real business. Same
 * per-currency treatment as {@link MAX_ADVANCE_EUR}.
 */
export const MAX_SANE_MONTHLY_REVENUE_EUR = 500_000_000;

const MAX_SANE_MONTHLY_REVENUE_BY_CURRENCY: Partial<Record<string, number>> = {
  DKK: 3_750_000_000,
  SEK: 5_500_000_000,
};

export function maxSaneMonthlyRevenueFor(currencyCode: string): number {
  return (
    MAX_SANE_MONTHLY_REVENUE_BY_CURRENCY[currencyCode] ??
    MAX_SANE_MONTHLY_REVENUE_EUR
  );
}

/**
 * Highest figure the revenue input accepts: one digit short of a billion.
 *
 * A ten-digit figure overflows the input and the estimate that reads from it,
 * so the field stops accepting keystrokes here rather than rendering a number
 * that breaks the layout. Digit count is currency-independent, which is why
 * this is a flat ceiling.
 */
export const MAX_ENTERABLE_MONTHLY_REVENUE = 999_999_999;

/**
 * Ceiling for the revenue input — the stricter of the layout ceiling and the
 * currency's plausibility bound. SEK and DKK are bounded in the billions, so
 * the layout ceiling governs there; EUR-family currencies stay on the tighter
 * plausibility bound.
 */
export function maxEnterableMonthlyRevenueFor(currencyCode: string): number {
  return Math.min(
    maxSaneMonthlyRevenueFor(currencyCode),
    MAX_ENTERABLE_MONTHLY_REVENUE
  );
}

/** Strong ease-out for GPU-friendly opacity/filter crossfades (design-eng). */
export const FUNDING_ELIGIBILITY_CROSSFADE =
  "transition-[opacity,filter] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none";

export const FUNDING_ELIGIBILITY_BORDER_CROSSFADE =
  "transition-opacity duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none";

/** Pause before eligible signup redirect so step 03 gradient can crossfade in. */
export const FUNDING_ELIGIBILITY_STEP_HANDOFF_MS = 300;

/** Step 02 email capture pill — shared Figma 4074:23618 spacing. */
export const FUNDING_ELIGIBILITY_APPLY_FORM_CLASS = cn(
  EMAIL_CAPTURE_PILL_FORM_CLASS,
  "border-[#caccce] bg-white"
);

export const FUNDING_ELIGIBILITY_APPLY_INPUT_CLASS = cn(
  EMAIL_CAPTURE_PILL_INPUT_CLASS,
  "placeholder:text-[#b0b3b5]"
);

export const FUNDING_ELIGIBILITY_MIN_REVENUE = 10_000;

/** Brief pause before the first funding estimate is revealed (with confetti). */
export const FUNDING_ELIGIBILITY_ESTIMATE_LOADING_MS = 900;

export type FundingEligibilityStatus = "incomplete" | "eligible" | "ineligible";

export function parseRevenue(raw: string): number | null {
  const cleaned = raw.replace(/,/g, "").replace(/[^0-9.]/g, "");
  if (!cleaned) return null;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** AUD in the eligibility checker uses bare "$" (en-AU), per WEB-721 / WEB-729. */
export function fundingEligibilityFormatLocale(
  country: string,
  locale: string
): string {
  return country === FUNDING_ELIGIBILITY_AUD_COUNTRY ? "en-AU" : locale;
}

/**
 * Revenue input prefix — "$" for Australia, not "A$".
 *
 * Falls back to the visiting locale's currency until a country is picked.
 */
export function fundingEligibilityCurrencySymbol(
  country: string,
  locale?: string
): string {
  return country === FUNDING_ELIGIBILITY_AUD_COUNTRY
    ? "$"
    : currencyFor(country, locale);
}

export function formatFundingEligibilityAmount(
  currency: string,
  amount: number,
  locale = "en",
  country = ""
): string {
  const formatLocale = fundingEligibilityFormatLocale(country, locale);
  return formatLocalizedCurrency(amount, formatLocale, {
    currency,
    compact: amount >= 1_000,
  });
}

/**
 * Rounding granularity, picked from the size of the estimate itself. A flat
 * step can't serve the whole range: 5k is false precision against a €4m figure
 * and, at the other end, wider than the entire 1–1.2x band for a €10k/month
 * business, which would collapse the range to a single repeated number.
 *
 * `below` is the exclusive upper edge of the bracket, in the estimate's own
 * currency. Each bound picks its own step from its own size, which keeps the
 * estimate monotonic: every edge is a multiple of both the finer and the
 * coarser step, so a bound can never round *down* as revenue rises past one.
 * Deriving a single shared step from one bound breaks that — the other bound
 * sits at an arbitrary offset and can tick backwards by up to half a step.
 */
const FUNDING_ELIGIBILITY_ROUNDING_STEPS: readonly {
  below: number;
  step: number;
}[] = [
  { below: 50_000, step: 1_000 },
  { below: 250_000, step: 5_000 },
  { below: Number.POSITIVE_INFINITY, step: 10_000 },
];

/** Rounding step for an estimate bound of roughly `amount`. */
export function fundingEligibilityRoundingStep(
  amount: number,
  currencyCode = "USD"
): number {
  const scale = fundingEligibilityBandScale(currencyCode);
  return (
    FUNDING_ELIGIBILITY_ROUNDING_STEPS.find(
      ({ below }) => amount < below * scale
    )!.step * scale
  );
}

/**
 * Round an estimate bound to the nearest step for its own size, never below one
 * full step — the counter card animates the estimate up from a near-zero
 * revenue, and a "€0–€0" first frame would read as a bug.
 */
export function roundFundingEligibilityEstimate(
  amount: number,
  currencyCode = "USD"
): number {
  const step = fundingEligibilityRoundingStep(amount, currencyCode);
  return Math.max(step, Math.round(amount / step) * step);
}

export function computeFundingEligibilityEstimate(
  revenueStr: string,
  currency: string,
  locale: string,
  country: string,
  upToPrefix = "up to ",
  category = ""
): string | null {
  const revenue = parseRevenue(revenueStr);
  if (revenue == null) return null;

  const formatLocale = fundingEligibilityFormatLocale(country, locale);
  const format = (amount: number) =>
    formatLocalizedCurrency(amount, formatLocale, { currency, compact: true });

  const { lower: lowerMultiplier, upper: upperMultiplier } =
    getFundingEligibilityMultipliers(category, revenue, currency);
  const cap = maxAdvanceFor(currency);
  const lower = roundFundingEligibilityEstimate(
    revenue * lowerMultiplier,
    currency
  );
  const upper = Math.min(
    Math.max(
      roundFundingEligibilityEstimate(revenue * upperMultiplier, currency),
      lower
    ),
    cap
  );

  // Both bounds hit the cap — a "€20M–€20M" range would be nonsensical, so
  // switch to a single "up to €20M" figure instead of a range.
  if (lower >= cap) {
    return `${upToPrefix}${format(cap)}`;
  }

  // A band narrower than one rounding step collapses to a single figure.
  // Repeating it as "€12k–€12k" reads as a bug, so show the one number.
  if (lower === upper) {
    return format(lower);
  }

  return `${format(lower)}–${format(upper)}`;
}

export function isFundingEligibilityFormFilled(
  revenue: string,
  category: string,
  country: string
): boolean {
  return (
    parseRevenue(revenue) !== null &&
    category.trim().length > 0 &&
    country.trim().length > 0
  );
}

export function isFundingEligibilityEligible(
  revenue: string,
  category: string,
  country: string
): boolean {
  const parsedRevenue = parseRevenue(revenue);
  return (
    parsedRevenue !== null &&
    parsedRevenue >= FUNDING_ELIGIBILITY_MIN_REVENUE &&
    category.trim().length > 0 &&
    country.trim().length > 0
  );
}

/** @deprecated Use `isFundingEligibilityEligible`. */
export function isFundingEligibilityComplete(
  revenue: string,
  category: string,
  country: string
): boolean {
  return isFundingEligibilityEligible(revenue, category, country);
}

export function getFundingEligibilityStatus(
  revenue: string,
  category: string,
  country: string
): FundingEligibilityStatus {
  if (!isFundingEligibilityFormFilled(revenue, category, country)) {
    return "incomplete";
  }

  if (isFundingEligibilityEligible(revenue, category, country)) {
    return "eligible";
  }

  return "ineligible";
}

export function buildFundingEligibilityIneligibleMessage(
  template: string,
  revenue: string,
  currency: string,
  locale = "en",
  country = ""
): string {
  const parsedRevenue = parseRevenue(revenue);
  const threshold = formatFundingEligibilityAmount(
    currency,
    FUNDING_ELIGIBILITY_MIN_REVENUE,
    locale,
    country
  );

  let message = template
    .replaceAll("{threshold}", threshold)
    .replaceAll("{country}", country.trim() || "your country");

  if (parsedRevenue === null) {
    return message;
  }

  const gap = formatFundingEligibilityAmount(
    currency,
    FUNDING_ELIGIBILITY_MIN_REVENUE - parsedRevenue,
    locale,
    country
  );

  return message.replaceAll("{gap}", gap);
}

export function formatFundingEligibilityRevenueDisplay(
  revenue: string,
  currency: string,
  locale = "en",
  country = ""
): string {
  const parsedRevenue = parseRevenue(revenue);
  if (parsedRevenue === null) {
    return "—";
  }

  return `${formatFundingEligibilityAmount(currency, parsedRevenue, locale, country)} / month`;
}
